import { useMemo, useState } from "react";
import { dublettenVorschlaege, fuehreZusammen, unterschiede } from "../dienste/zusammenfuehren.js";
import { markiereGetrennt, offeneEntscheidungen } from "../dienste/dubletten.js";
import { uebernehmeRegion, uebernehmeWein, uebernehmeWinzer } from "../dienste/katalog.js";
import { rebsortenText } from "../kern/fachlogik.js";
import { fmtZahl, sortDe } from "../kern/format.js";
import { TYPEN } from "../kern/konstanten.js";
import { landName } from "../i18n/laender.js";
import { Auswahl, Btn, Feld, Leer, SuchAuswahl, TextKnopf } from "../ui/primitive.jsx";
import { t } from "../i18n/index.js";

/* ================================================================
   Objekte zusammenführen — für die eigenen Daten.

   Die Quelle geht im Ziel auf: Bestände werden addiert, alle
   Verweise wandern mit, die Quelle verschwindet. Vor dem Ausführen
   gibt es eine Vorschau, danach ist es endgültig.
   ================================================================ */

/* Beschriftung erst beim Zeichnen übersetzen, damit ein
   Sprachwechsel sofort greift. */
const ARTEN = [["wein", "Weine"], ["winzer", "Winzer"], ["region", "Regionen"]];
const FELD = { wein: "weine", winzer: "winzer", region: "regions" };


/* Ein Name allein reicht nicht, um zwei „Scharzhofberger" zu
   unterscheiden — erst Winzer, Region und Land machen sie
   auseinanderhaltbar. `quelle` ist der Bestand, in dem nachgeschlagen
   wird: das eigene Kellerbuch oder der Katalog. */
function beschreibung(quelle, typ, o) {
  if (!o || !quelle) return "";
  /* Kellerbuch: `regions`, Katalog: `regionen` — sonst fehlten bei
     Katalogkandidaten Region und Land. */
  const regionVon = (id) => (quelle.regions || quelle.regionen || []).find((r) => r.id === id) || null;
  if (typ === "wein") {
    const wz = (quelle.winzer || []).find((x) => x.id === o.winzerId) || null;
    const r = regionVon(o.regionId) || (wz ? regionVon(wz.regionId) : null);
    return [wz && wz.name, r && r.name, r && r.land ? landName(r.land) : null]
      .filter(Boolean).join(" · ");
  }
  if (typ === "winzer") {
    const r = regionVon(o.regionId);
    return [o.ort, r && r.name, r && r.land ? landName(r.land) : null]
      .filter(Boolean).join(" · ");
  }
  return o.land ? landName(o.land) : "";
}


/* Rohwerte lesbar machen: IDs werden zu Namen, Schlüssel zu
   Beschriftungen. Ohne das stünde im Vergleich „w_a1b2c3" gegen
   „w_d4e5f6" — richtig, aber unbrauchbar. */
function zeigeWert(db, art, wert) {
  if (wert === null || wert === undefined || wert === ""
      || (Array.isArray(wert) && wert.length === 0)) return "—";
  switch (art) {
    case "winzer": {
      const o = (db.winzer || []).find((x) => x.id === wert);
      return o ? o.name : String(wert);
    }
    case "region": {
      const o = (db.regions || []).find((x) => x.id === wert);
      return o ? o.name : String(wert);
    }
    case "land": return landName(wert);
    case "typ": return TYPEN[wert] ? t(TYPEN[wert].label) : String(wert);
    case "rebsorten": return rebsortenText({ rebsorten: wert }) || "—";
    case "zahl": return fmtZahl(wert, 4);
    default: return String(wert);
  }
}


/* Eine Zeile des Feldvergleichs. */
function VergleichsZeile({ db, zeile }) {
  const marke = { gleich: "gleich", ergaenzt: "wird ergänzt", konflikt: "abweichend" }[zeile.status];
  return (
    <div className={"wl-zf-vgl-zeile ist-" + zeile.status}>
      <span className="wl-zf-vgl-feld">
        {zeile.jahr != null ? `${zeile.jahr} · ` : ""}{t(zeile.label)}
        <span className="wl-zf-vgl-marke">{t(marke)}</span>
      </span>
      <span className="wl-zf-vgl-wert">{zeigeWert(db, zeile.art || "text", zeile.quelle)}</span>
      <span className="wl-zf-vgl-wert">{zeigeWert(db, zeile.art || "text", zeile.ziel)}</span>
    </div>
  );
}

function ZusammenfuehrenPanel({ db, setDb, katalog = null, istAdmin = false, onClose }) {
  const [typ, setTyp] = useState("wein");
  const [quelleId, setQuelleId] = useState("");
  const [zielId, setZielId] = useState("");
  const [bericht, setBericht] = useState(null);
  const [fertig, setFertig] = useState(null);
  const [rohSichtbar, setRohSichtbar] = useState(false);

  const liste = useMemo(
    () => [...(db[FELD[typ]] || [])].sort(sortDe), [db, typ]);

  /* Offene Dublettenfragen gegenüber dem Katalog. Solange sie
     unbeantwortet sind, wird das Objekt nicht vorgeschlagen. */
  const offen = useMemo(() => offeneEntscheidungen(db, katalog), [db, katalog]);

  /* Zusammenführen mit einem Katalogeintrag: erst übernehmen, damit
     das Ziel lokal existiert, dann die eigene Fassung darin auflösen. */
  const mitKatalogVereinen = (eintrag, zielId) => {
    setDb((prev) => {
      const holen = eintrag.typ === "wein" ? uebernehmeWein
        : eintrag.typ === "winzer" ? uebernehmeWinzer : uebernehmeRegion;
      const nachUebernahme = holen(prev, katalog, zielId);
      if (!nachUebernahme.id) return prev;
      const erg = fuehreZusammen(nachUebernahme.db, eintrag.typ, eintrag.objekt.id, zielId);
      return erg.bericht ? erg.db : nachUebernahme.db;
    });
  };

  const getrenntLassen = (eintrag) =>
    setDb((prev) => markiereGetrennt(prev, eintrag.typ, eintrag.objekt.id));

  /* „Alle zusammenführen“ nimmt nur die sicheren Fälle — eine bloß
     mögliche Dublette entscheidet der Nutzer einzeln. */
  const sichere = offen.filter((e) => e.sperrt);
  const alleVereinen = () => { for (const e of sichere) mitKatalogVereinen(e, e.kandidaten[0].objekt.id); };
  const alleGetrennt = () => { for (const e of offen) getrenntLassen(e); };
  const dubletten = useMemo(() => dublettenVorschlaege(db, typ), [db, typ]);

  const optionen = liste.map((o) => ({ wert: o.id, label: o.name, neben: beschreibung(db, typ, o) }));
  const quelle = liste.find((o) => o.id === quelleId) || null;
  const ziel = liste.find((o) => o.id === zielId) || null;

  const wechsle = (t) => { setTyp(t); setQuelleId(""); setZielId(""); setBericht(null); setFertig(null); setRohSichtbar(false); };

  /* Feldvergleich — steht schon vor der Vorschau bereit, damit man
     beim Auswählen sieht, worauf man sich einlässt. */
  const diff = useMemo(
    () => (quelleId && zielId && quelleId !== zielId ? unterschiede(db, typ, quelleId, zielId) : null),
    [db, typ, quelleId, zielId]);

  const vorschau = () => {
    if (!quelle || !ziel || quelleId === zielId) return;
    const { bericht: b } = fuehreZusammen(db, typ, quelleId, zielId);
    setBericht(b);
  };

  const ausfuehren = () => {
    if (!bericht) return;
    setDb((prev) => fuehreZusammen(prev, typ, quelleId, zielId).db);
    setFertig(bericht);
    setBericht(null);
    setQuelleId(""); setZielId("");
  };

  const tauschen = () => { setQuelleId(zielId); setZielId(quelleId); setBericht(null); };

  return (
    <div className="wl-teil">
      <p className="wl-teil-info">
        Liegt etwas doppelt vor, geht die <strong>{t("Quelle")}</strong> im <strong>{t("Ziel")}</strong> auf.
        Bestände werden addiert, Kritiken, Entnahmen, Bewegungen und Weinkarten hängen
        automatisch um. Die Quelle wird dabei gelöscht — das lässt sich nicht rückgängig
        machen.
      </p>

      {katalog && (
        <div className="wl-zf-abgleich">
          <span className="wl-konto-titel">{t("Offene Entscheidungen")}{offen.length ? ` · ${offen.length}` : ""}</span>
          {offen.length === 0
            ? <p className="wl-teil-info">{t("Keine offenen Entscheidungen.")}</p>
            : (
              <>
                <p className="wl-teil-info">
                  {t("Ähnliche Einträge liegen schon im Katalog. Entscheide je Eintrag, ob zusammengeführt oder getrennt geführt wird. Eine wahrscheinliche Dublette wird bis dahin nicht vorgeschlagen, eine mögliche schon.")}
                </p>
                <ul className="wl-zf-offen">
                  {offen.map((e) => (
                    <li key={e.objekt.id}>
                      <div className="wl-zf-offenkopf">
                        <span className="wl-vs-name">{e.objekt.name}</span>
                        <span className={"wl-vs-status" + (e.sperrt ? " ist-dublette" : "")}>
                          {e.sperrt ? t("wahrscheinlich Dublette") : t("mögliche Dublette")}
                        </span>
                        <span className="wl-vs-meta">{beschreibung(db, e.typ, e.objekt)}</span>
                      </div>
                      <div className="wl-vs-aktionen wl-zf-kandidaten">
                        {e.kandidaten.slice(0, 3).map((k) => {
                          const naeher = beschreibung(katalog, e.typ, k.objekt);
                          return (
                            <span key={k.objekt.id} className="wl-zf-kandidat">
                              <TextKnopf onClick={() => mitKatalogVereinen(e, k.objekt.id)}>
                                {t("Zusammenführen mit …")} {k.objekt.name}
                                {naeher ? ` (${naeher})` : ""}
                              </TextKnopf>
                              <span className="wl-vs-meta">{k.merkmale.map((x) => t(x)).join(" · ")}</span>
                            </span>
                          );
                        })}
                        <TextKnopf className="wl-teil-widerruf" onClick={() => getrenntLassen(e)}>
                          {t("Getrennt lassen")}
                        </TextKnopf>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="wl-vs-aktionen">
                  {sichere.length > 0 && (
                    <Btn variante="linie" onClick={alleVereinen}>
                      {t("Alle wahrscheinlichen zusammenführen ({n})", { n: sichere.length })}
                    </Btn>
                  )}
                  <Btn variante="linie" onClick={alleGetrennt}>{t("Alle getrennt lassen")}</Btn>
                </div>
              </>
            )}
          <div className="wl-teil-trenner" />
        </div>
      )}

      <div className="wl-zf-arten" role="tablist">
        {ARTEN.map(([wert, label]) => (
          <button key={wert} role="tab" aria-selected={typ === wert}
            className={typ === wert ? "aktiv" : ""} onClick={() => wechsle(wert)}>{t(label)}</button>
        ))}
      </div>

      {fertig && (
        <p className="wl-konto-ok">
          „{fertig.quelle}" ist in „{fertig.ziel}" aufgegangen — {fertig.verweise} Verweise umgehängt
          {fertig.jahrgaenge ? `, ${fertig.jahrgaenge} Jahrgänge zusammengelegt` : ""}.
        </p>
      )}

      {dubletten.length > 0 && (
        <div className="wl-zf-dubletten">
          <span className="wl-konto-titel">{t("Mögliche Dubletten")}</span>
          <ul>
            {dubletten.slice(0, 12).map((gruppe, i) => (
              <li key={i}>
                {/* Jeder Eintrag ist anklickbar und wird dann zur Quelle;
                    Ziel wird der erste andere. Ohne Winzer, Region und
                    Land wäre die Gruppe nicht auseinanderzuhalten —
                    drei Zeilen „Scharzhofberger" helfen niemandem. */}
                <ul className="wl-zf-dubgruppe">
                  {gruppe.map((o) => (
                    <li key={o.id}>
                      <TextKnopf onClick={() => {
                        const ziel = gruppe.find((x) => x.id !== o.id);
                        setQuelleId(o.id); setZielId(ziel ? ziel.id : "");
                        setBericht(null); setFertig(null);
                      }}>{o.name}</TextKnopf>
                      <span className="wl-vs-meta">{beschreibung(db, typ, o) || t("ohne weitere Angaben")}</span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Feld label={t("Quelle — geht auf und verschwindet")} breit>
        <SuchAuswahl value={quelleId} platzhalter={t("Auswählen …")}
          onChange={(e) => { setQuelleId(e.target.value); setBericht(null); }} optionen={optionen} />
      </Feld>
      <Feld label={t("Ziel — bleibt bestehen")} breit>
        <SuchAuswahl value={zielId} platzhalter={t("Auswählen …")}
          onChange={(e) => { setZielId(e.target.value); setBericht(null); }} optionen={optionen} />
      </Feld>

      {quelleId && zielId && quelleId === zielId && (
        <p className="wl-fehler">{t("Quelle und Ziel müssen verschieden sein.")}</p>
      )}

      {/* Feldvergleich: was ist gleich, was wird ergänzt, was
          widerspricht sich. Steht vor der Vorschau, weil man das
          wissen will, bevor man auf „Vorschau" drückt. */}
      {diff && (
        <div className="wl-zf-vergleich">
          <p className={"wl-zf-urteil" + (diff.verlustfrei ? " ist-gut" : " ist-warnung")}>
            {diff.verlustfrei
              ? t("Keine widersprüchlichen Angaben — beim Zusammenführen bleibt alles erhalten und beide Seiten ergänzen sich.")
              : t.plural(diff.konflikte.length,
                  "Eine Angabe widerspricht sich. Sie wird vom Ziel übernommen, die der Quelle geht verloren.",
                  "{n} Angaben widersprechen sich. Sie werden vom Ziel übernommen, die der Quelle gehen verloren.")}
          </p>

          <div className="wl-zf-vgl">
            <div className="wl-zf-vgl-kopf">
              <span className="wl-zf-vgl-feld">{t("Merkmal")}</span>
              <span className="wl-zf-vgl-wert">{t("Quelle")}</span>
              <span className="wl-zf-vgl-wert">{t("Ziel")}</span>
            </div>
            {diff.felder.map((z) => <VergleichsZeile key={z.feld} db={db} zeile={z} />)}
            {diff.jahrgaenge.map((z) => (
              <VergleichsZeile key={`${z.jahr}-${z.feld}`} db={db} zeile={z} />
            ))}
          </div>

          {/* Rohansicht: für den Fall, dass die aufbereitete Sicht
              nicht ausreicht — etwa bei Katalogverweisen oder Feldern,
              die hier gar nicht vorkommen. */}
          {istAdmin && (
            <div className="wl-zf-roh">
              <TextKnopf onClick={() => setRohSichtbar((v) => !v)}>
                {rohSichtbar ? t("Rohdaten ausblenden") : t("Rohdaten anzeigen")}
              </TextKnopf>
              {rohSichtbar && (
                <div className="wl-zf-rohpaar">
                  <div>
                    <span className="wl-zf-rohtitel">{t("Quelle")} · {diff.quelle.name}</span>
                    <pre>{JSON.stringify(diff.quelle, null, 2)}</pre>
                  </div>
                  <div>
                    <span className="wl-zf-rohtitel">{t("Ziel")} · {diff.ziel.name}</span>
                    <pre>{JSON.stringify(diff.ziel, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="wl-vs-aktionen">
        <Btn variante="linie" onClick={tauschen} disabled={!quelleId || !zielId}>{t("Richtung tauschen")}</Btn>
        <Btn variante="voll" onClick={vorschau}
          disabled={!quelle || !ziel || quelleId === zielId}>{t("Vorschau")}</Btn>
      </div>

      {bericht && (
        <div className="wl-zf-vorschau">
          <p>
            „{bericht.quelle}" geht in „{bericht.ziel}" auf: {bericht.verweise} Verweise werden
            umgehängt{bericht.jahrgaenge ? `, ${bericht.jahrgaenge} Jahrgänge zusammengelegt` : ""}.
          </p>
          {diff && !diff.verlustfrei && (
            <p className="wl-zf-urteil ist-warnung">
              {t.plural(diff.konflikte.length,
                "Eine abweichende Angabe der Quelle geht dabei verloren.",
                "{n} abweichende Angaben der Quelle gehen dabei verloren.")}
            </p>
          )}
          <Btn variante="voll" onClick={ausfuehren}>{t("Jetzt zusammenführen")}</Btn>
        </div>
      )}

      {liste.length === 0 && <Leer text={t("Hier gibt es noch nichts zusammenzuführen.")} />}

      <div className="wl-teil-fuss">
        <Btn variante="linie" onClick={onClose}>{t("Schließen")}</Btn>
      </div>
    </div>
  );
}

export { ZusammenfuehrenPanel };
